import { FC, useMemo } from 'react';
import { upperCase } from 'lodash';
import classNames from 'classnames';

import {
  Container,
  Heading,
  Link,
  Portal,
  RatingStats,
  Sharing,
} from '@/components';
import { useUiStore } from '@/data';
import { CustomerReview, GProduct, Store } from '@/types';
import { NOT_DEFINED } from '@/constants';
import { useFeaturedFlag, useIsMobile } from '@/hooks';
import { formatSpecialFlowerType } from '@/utils';

import { ProductImages } from './ProductImages';
import { ProductBadges } from './ProductBadges';
import { ReviewsFilterProvider } from './ReviewsFilterProvider';
import { ProductFeats } from './ProductFeats';
import { ProductDescription } from './ProductDescription';
import { ProductExtraDescriptions } from './ProductExtraDescriptions';
import { ProductWarning } from './ProductWarning';
import { ProductControls } from './ProductControls';
import { AboutProduct } from './AboutProduct';
import { ProductReviewsFilters } from './ProductReviewsFilters';
import { FilteredTabReviews } from './FilteredTabReviews';
import { NewProductBogo } from './ProductBogo';
import { LabResults } from './LabResults';
import { useProductContext } from '../Product/context';

interface IProductWithUrls extends GProduct {
  brand_url: string;
  category_url: string;
}

interface IProductDetailProps {
  product: IProductWithUrls;
  reviews: CustomerReview[];
  store: Store;
  isProductAvailable?: boolean;
}
const ProductDetail: FC<IProductDetailProps> = ({
  product,
  reviews,
  store,
  isProductAvailable,
}) => {
  const { isMobile } = useIsMobile();
  const [state, _] = useUiStore();
  const { disableProductReviews } = useFeaturedFlag();
  const { isSpecial, weightVariant } = useProductContext();

  const brandLink = (
    <Link
      aria-label={`Go to ${product.brand_name}`}
      className="details__link"
      href={product.brand_url}
    >
      {product.brand_name || NOT_DEFINED}
    </Link>
  );

  // Checks if any other portal/modal is currently open in the UI
  // We prevent showing the mobile product controls when another portal is open
  // to avoid UI conflicts and overlapping modals
  // Note: We exclude promotion bar since it doesn't interfere with product controls
  const isOtherPortalOpen = useMemo(
    () =>
      Object.entries(state).some(
        ([key, value]) =>
          key.startsWith('is_') &&
          key !== 'is_promotionbar_open' &&
          typeof value === 'boolean' &&
          value === true,
      ),
    [state],
  );

  return (
    <ReviewsFilterProvider reviews={reviews}>
      <section className={'details'}>
        <Container>
          <div className={'container'}>
            <ProductImages product={product} />
            <div className={'details__body'}>
              <NewProductBogo product={product} />

              <ProductBadges
                isSpecial={isSpecial}
                product={product}
                weightVariant={weightVariant}
              />
              <span className={'details__span'}>
                {!product?.brand_name && NOT_DEFINED}
                {product?.brand_name && brandLink}
              </span>
              <Heading className="details__product_heading" level={1}>
                {product.name}
              </Heading>
              {product?.flower_type ? (
                <span
                  className={classNames(
                    'flower__type',
                    'type__default',
                    `type__${product?.flower_type
                      .toLocaleLowerCase()
                      .replace(/\s+/g, '')}`,
                  )}
                >
                  {product?.flower_type === 'cbd'
                    ? upperCase(product?.flower_type)
                    : formatSpecialFlowerType(product?.flower_type) ||
                      'not specified'}
                </span>
              ) : null}
              <ProductFeats product={product} />
              <ProductDescription product={product} />
              <ProductExtraDescriptions product={product} />
              <ProductWarning product={product} />
              <ProductControls
                isProductAvailable={isProductAvailable}
                isSpecial={isSpecial}
                product={product}
                weightVariant={weightVariant}
              />
              <AboutProduct product={product} />
              <Heading className={'details__subtitle'} level={4}>
                Share
              </Heading>
              <Sharing
                className={'sharing'}
                include={['facebook', 'twitter', 'pinterest', 'whatsapp']}
                media={product.asset?.image as string}
                url={`${store.domain}${product.url}`}
              />
            </div>
          </div>
          <LabResults labResults={product.labResults} />
          {reviews && !disableProductReviews && (
            <div className="details__rating-reviews">
              <RatingStats
                className={'rating__component'}
                review_rating={product?.review_rating || 0}
                reviews={reviews || []}
                total_reviews={product?.reviews || 0}
              />
              <ProductReviewsFilters reviews={reviews} />
            </div>
          )}
        </Container>
        {!disableProductReviews && <FilteredTabReviews />}
      </section>
      {isMobile && !isOtherPortalOpen && (
        <Portal
          activeSnapPoint={'120px'}
          classContent="product__detail-control_modal"
          direction="bottom"
          disablePreventScroll={true}
          ignoreOutsideClick={true}
          isOpen={true}
          nested={true}
          noBodyStyles={true}
          showIconMore={true}
          showIconTouch={false}
          snapPoints={['120px', '280px', '436px', 0.8]}
          toggle={() => {}}
        >
          <ProductControls
            isProductAvailable={isProductAvailable}
            isSpecial={isSpecial}
            product={product}
            weightVariant={weightVariant}
          />
        </Portal>
      )}
    </ReviewsFilterProvider>
  );
};

export { ProductDetail };
